// Peak detection for vibration FFT spectra. Pure functions, no rendering and
// no React — unit tested in isolation (see fft-peaks.test.js). The renderer
// consumes `detectPeaks` and `annotatePeakOrders` and only draws the result.

/**
 * @typedef {import('../types/fft-spectrum').FftSpectrum} FftSpectrum
 * @typedef {Object} FftPeak
 * @property {number} binIndex
 * @property {number} freq       Center frequency in Hz (binIndex * binStep).
 * @property {number} amplitude
 * @property {string} [order]    Order/BPF label (e.g. "1x", "2x", "BPF").
 */

// Median of a numeric array. Copies before sorting so the caller's array is
// left untouched. Returns 0 for an empty array.
export const median = (values) => {
  if (!values || !values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
};

/**
 * Detect the N most prominent spectral peaks.
 *
 * A bin qualifies as a peak when it is a strict local maximum and its
 * amplitude is above `medianFactor` times the spectrum median (the "noise
 * floor" reference). The DC bin (index 0) is skipped — it is not vibration.
 *
 * @param {number[]} magnitudes
 * @param {Object} [opts]
 * @param {number} [opts.binStep=1]       Hz per bin, used to compute freq.
 * @param {number} [opts.count=5]         Max peaks returned (the N largest).
 * @param {number} [opts.medianFactor=3]  Threshold = medianFactor * median.
 * @param {boolean} [opts.skipDc=true]    Ignore bin 0.
 * @returns {FftPeak[]} peaks sorted by descending amplitude.
 */
export const detectPeaks = (magnitudes, opts = {}) => {
  const {
    binStep = 1,
    count = 5,
    medianFactor = 3,
    skipDc = true,
  } = opts;
  if (!Array.isArray(magnitudes) || magnitudes.length < 3) return [];

  const start = skipDc ? 1 : 0;
  // Median reference excludes the DC bin so a large DC offset cannot inflate
  // the noise floor and hide real peaks.
  const reference = magnitudes.slice(start);
  const threshold = median(reference) * medianFactor;

  const candidates = [];
  for (let i = Math.max(start, 1); i < magnitudes.length - 1; i += 1) {
    const value = magnitudes[i];
    // Strict local maximum (>= on the right lets a flat-topped peak register
    // once on its rising edge instead of being skipped entirely).
    if (value > magnitudes[i - 1] && value >= magnitudes[i + 1] && value > threshold) {
      candidates.push({ binIndex: i, freq: i * binStep, amplitude: value });
    }
  }

  candidates.sort((a, b) => b.amplitude - a.amplitude);
  return candidates.slice(0, Math.max(0, count));
};

/**
 * Label peaks with their rotational order (1x, 2x, 3x) and the blade-pass
 * frequency (BPF) when a nominal rpm is known. A peak is tagged when its
 * frequency falls within ±1 bin of the target frequency.
 *
 * @param {FftPeak[]} peaks
 * @param {Object} [opts]
 * @param {number} [opts.rpm]
 * @param {number} [opts.bladeCount]
 * @param {number} [opts.binStep=1]
 * @param {number[]} [opts.orders=[1,2,3]]
 * @returns {FftPeak[]} new array; each peak may gain an `order` field.
 */
export const annotatePeakOrders = (peaks, opts = {}) => {
  const { rpm, bladeCount, binStep = 1, orders = [1, 2, 3] } = opts;
  if (!Array.isArray(peaks) || !peaks.length) return peaks || [];
  if (!Number.isFinite(rpm) || rpm <= 0 || !Number.isFinite(binStep) || binStep <= 0) {
    return peaks.map((p) => ({ ...p }));
  }

  const oneX = rpm / 60; // Hz
  const tolerance = binStep; // ±1 bin
  const targets = orders
    .filter((n) => Number.isFinite(n) && n > 0)
    .map((n) => ({ freq: oneX * n, label: `${n}x` }));
  if (Number.isFinite(bladeCount) && bladeCount > 0) {
    targets.push({ freq: oneX * bladeCount, label: 'BPF' });
  }

  return peaks.map((peak) => {
    let best = null;
    let bestDiff = Infinity;
    targets.forEach((t) => {
      const diff = Math.abs(peak.freq - t.freq);
      if (diff <= tolerance && diff < bestDiff) {
        bestDiff = diff;
        best = t.label;
      }
    });
    return best ? { ...peak, order: best } : { ...peak };
  });
};

/**
 * Convenience wrapper: detect + annotate straight from a spectrum payload.
 * @param {FftSpectrum} spectrum
 * @param {Object} [opts]
 * @returns {FftPeak[]}
 */
export const findSpectrumPeaks = (spectrum, opts = {}) => {
  if (!spectrum || !Array.isArray(spectrum.magnitudes)) return [];
  const peaks = detectPeaks(spectrum.magnitudes, {
    binStep: spectrum.binStep,
    ...opts,
  });
  return annotatePeakOrders(peaks, {
    rpm: spectrum.rpm,
    bladeCount: spectrum.bladeCount,
    binStep: spectrum.binStep,
    ...opts,
  });
};
